import { v4 as uuid } from 'uuid';

import {
  WEBCHAT_CUSTOMER_IDENTIFIER_PREFIX,
  WEBCHAT_MAX_STORED_CHAT_SESSIONS,
} from 'modules/webchat/domain/constants';
import { IWebchatChatSession } from 'modules/webchat/domain/entities';
import { IWebchatChatSessionsRepository } from 'modules/webchat/domain/repositories';
import IStorageProvider from 'shared/domain/providers/IStorageProvider';

const isChatSession = (value: unknown): value is IWebchatChatSession => {
  if (typeof value !== 'object' || value === null) return false;

  const { identifier, sessionId } = value as Record<string, unknown>;

  return typeof identifier === 'string' && typeof sessionId === 'string';
};

/**
 * Salas guardadas no navegador. As de cliente ficam para o cliente reencontrar
 * a conversa ao entrar de novo; a de visitante só vale enquanto a identidade
 * do pré-chat existir, então as de outros visitantes são descartadas.
 */
export default class LocalStorageWebchatChatSessionsRepository
  implements IWebchatChatSessionsRepository
{
  constructor(private storageProvider: IStorageProvider) {}

  public findOrCreate: IWebchatChatSessionsRepository['findOrCreate'] =
    identifier => {
      const sessions = this.read();
      const current = sessions.find(
        session => session.identifier === identifier,
      );

      if (current) return current.sessionId;

      const session: IWebchatChatSession = { identifier, sessionId: uuid() };

      this.write([...this.keepCustomers(sessions), session]);

      return session.sessionId;
    };

  public forget: IWebchatChatSessionsRepository['forget'] = identifier => {
    const sessions = this.read();

    if (!sessions.some(session => session.identifier === identifier)) return;

    this.write(sessions.filter(session => session.identifier !== identifier));
  };

  private keepCustomers(sessions: IWebchatChatSession[]) {
    return sessions
      .filter(session =>
        session.identifier.startsWith(WEBCHAT_CUSTOMER_IDENTIFIER_PREFIX),
      )
      .slice(-(WEBCHAT_MAX_STORED_CHAT_SESSIONS - 1));
  }

  private read(): IWebchatChatSession[] {
    try {
      const stored: unknown = this.storageProvider.get('WEBCHAT_CHAT_SESSIONS');

      return Array.isArray(stored) ? stored.filter(isChatSession) : [];
    } catch {
      return [];
    }
  }

  private write(sessions: IWebchatChatSession[]) {
    try {
      this.storageProvider.store('WEBCHAT_CHAT_SESSIONS', sessions);
    } catch {
      // Sem storage, a sala vale só enquanto a página estiver aberta.
    }
  }
}
